import { useState } from 'react';
import type { DeviceDetail } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import type { Guard } from '../pages/Device';

interface Props {
  readonly session: StoredSession;
  readonly device: DeviceDetail;
  readonly guard: Guard;
  readonly onChanged: () => void;
}

/**
 * The two device-wide switches, and the link the first one hands out.
 *
 * These override the per-person settings on the People tab rather than sitting
 * alongside them — which is exactly why they live on their own page with the
 * consequence written out underneath each one.
 */
export const SharingTab = ({ session, device, guard, onChanged }: Props) => {
  const [sharing, setSharing] = useState(device.sharing);
  const [copied, setCopied] = useState(false);

  const save = (changes: { publicUpload?: boolean; autoAcceptEveryone?: boolean }) =>
    guard(async () => {
      const next = await api.saveSharing(session.token, device.id, {
        publicUpload: changes.publicUpload ?? sharing.publicUpload,
        autoAcceptEveryone: changes.autoAcceptEveryone ?? sharing.autoAcceptEveryone,
      });
      setSharing(next);
      setCopied(false);
      onChanged();
    });

  return (
    <section className="stack-loose">
      <div>
        <div className="section-head">
          <p className="eyebrow">Anyone with the link</p>
        </div>

        <div className="stack">
          <label className="check">
            <input
              type="checkbox"
              checked={sharing.publicUpload}
              onChange={(event) => void save({ publicUpload: event.target.checked })}
            />
            Allow anyone to send photos if they know the link
          </label>
          <p className="note">
            No account needed — they type a name and pick photos. Turning this on overrides the
            per-person “can send” switches, because it already lets in more people than they do.
          </p>

          {sharing.publicUpload && sharing.publicUrl && (
            <div className="copyline">
              <code>{sharing.publicUrl}</code>
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() => {
                  if (!sharing.publicUrl) return;
                  void navigator.clipboard.writeText(sharing.publicUrl).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                type="button"
                className="button button-danger button-small"
                onClick={() =>
                  void guard(async () => {
                    const next = await api.rotatePublicLink(session.token, device.id);
                    setSharing((current) => ({ ...current, publicUrl: next.publicUrl }));
                    setCopied(false);
                  })
                }
              >
                New link
              </button>
            </div>
          )}
          {sharing.publicUpload && (
            <p className="note">
              A new link stops the old one working immediately. Use it if the link ends up
              somewhere you did not intend.
            </p>
          )}
        </div>
      </div>

      <div>
        <div className="section-head">
          <p className="eyebrow">Approval</p>
        </div>

        <div className="stack">
          <label className="check">
            <input
              type="checkbox"
              checked={sharing.autoAcceptEveryone}
              onChange={(event) => void save({ autoAcceptEveryone: event.target.checked })}
            />
            Auto-accept photos from anyone
          </label>
          <p className="note">
            {sharing.autoAcceptEveryone
              ? 'Everything sent appears on the TV straight away, whoever sent it, and the per-person approval switches are being overridden.'
              : 'Photos from anyone not on the auto-accept list wait on the Waiting tab until you or a delegate put them up.'}
          </p>
        </div>
      </div>
    </section>
  );
};
